import React, { useRef, useMemo, useLayoutEffect, useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useMatches } from 'react-router-dom';
import { useMsalAuthState, DISABLED_AUTH_STATE, type AuthState, isAuthDisabled } from '@/auth';
import { useAllNavItems, getNavItemsForBasePath } from '@/hooks/use-all-nav-items';
import { normalizeNavItems } from '@/utils/route-utils';
import { useAllUserMenuItems } from '@/hooks/use-all-user-menu-items';
import { SidebarHost } from '@/components/module-federation/sidebar-host/sidebar-host';
import { PendoProvider } from '@/components/pendo/pendo-provider';
import { FloatingRootProvider } from '@aero/ui';
import { env, type AppConfig } from '@/config/env';
import type { RouteHandle } from '@/config/router';

const MOBILE_BREAKPOINT = 768;

function useIsMobile(): boolean {
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);

    useEffect(() => {
        const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, []);

    return isMobile;
}

/**
 * Hook to get the active app configuration from the current route.
 *
 * This hook is the bridge between the router and the shell layout. It extracts
 * the `appConfig` that was attached to the route via the `handle` property
 * in `router.tsx`.
 *
 * ## How it works
 *
 * 1. `useMatches()` returns all matched route segments from root to current route
 * 2. We extract `appConfig` from each route's `handle` property
 * 3. We take the last (deepest) config, allowing child routes to override parents
 *
 * ## Example
 *
 * For URL `/app-template/products`, useMatches() returns:
 * ```
 * [
 *   { handle: undefined },                    // RootLayout
 *   { handle: undefined },                    // AuthLayout
 *   { handle: undefined },                    // ShellLayout
 *   { handle: { appConfig: { appName: "App Template", ... } } }  // app-template route
 * ]
 * ```
 *
 * After processing:
 * ```
 * .map(...)    → [undefined, undefined, undefined, { appName: "React Demo" }]
 * .filter(...) → [{ appName: "React Demo" }]
 * .pop()       → { appName: "React Demo" }
 * ```
 *
 * ## Why `.pop()` (deepest match)?
 *
 * If nested routes both have appConfig, the child's config takes precedence.
 * This allows for route-specific overrides of app settings.
 *
 * @returns The AppConfig for the current route, or null if none found
 * @see router.tsx - Where routes are created with handle.appConfig
 */
function useActiveAppConfig(): AppConfig | null {
    const matches = useMatches();

    // Find the deepest route with an appConfig handle
    const activeConfig =
        matches
            .map(match => (match.handle as RouteHandle | undefined)?.appConfig)
            .filter((config): config is AppConfig => config !== undefined)
            .pop() ?? null;

    return activeConfig;
}

type SidebarCollapseState = {
    isCollapsed: boolean;
    lastAutoCollapsedPath: string | null;
};

type SidebarCollapseAction = { type: 'NAVIGATE'; basePath: string | undefined; shouldAutoCollapse: boolean } | { type: 'TOGGLE' };

function sidebarCollapseReducer(state: SidebarCollapseState, action: SidebarCollapseAction): SidebarCollapseState {
    switch (action.type) {
        case 'NAVIGATE': {
            const currentPath = action.basePath ?? '';
            const isNewPath = state.lastAutoCollapsedPath !== currentPath;

            if (action.shouldAutoCollapse && isNewPath) {
                return {
                    isCollapsed: true,
                    lastAutoCollapsedPath: currentPath
                };
            }
            return state;
        }
        case 'TOGGLE':
            return { ...state, isCollapsed: !state.isCollapsed };
        default:
            return state;
    }
}

/**
 * Hook to manage sidebar collapse state with auto-collapse on app navigation.
 */
function useSidebarAutoCollapse(activeConfig: AppConfig | null) {
    const currentBasePath = activeConfig?.basePath;
    const shouldAutoCollapse = activeConfig?.collapseSidebarOnLoad ?? false;

    const [state, dispatch] = React.useReducer(sidebarCollapseReducer, {
        isCollapsed: shouldAutoCollapse,
        lastAutoCollapsedPath: shouldAutoCollapse ? (currentBasePath ?? '') : null
    });

    const prevBasePathRef = useRef(currentBasePath);
    useLayoutEffect(() => {
        if (prevBasePathRef.current !== currentBasePath) {
            dispatch({ type: 'NAVIGATE', basePath: currentBasePath, shouldAutoCollapse });
            prevBasePathRef.current = currentBasePath;
        }
    }, [currentBasePath, shouldAutoCollapse]);

    const toggle = () => dispatch({ type: 'TOGGLE' });

    return { isCollapsed: state.isCollapsed, toggle };
}

type ShellLayoutContentProps = {
    authState: AuthState;
};

/**
 * Shell layout content - the actual layout implementation.
 */
function ShellLayoutContent({ authState }: ShellLayoutContentProps) {
    const activeConfig = useActiveAppConfig();
    const location = useLocation();
    const { isCollapsed: isSidebarCollapsed, toggle: toggleSidebar } = useSidebarAutoCollapse(activeConfig);
    const isMobile = useIsMobile();
    const [openMobile, setOpenMobile] = useState(false);

    // Close mobile drawer on route change
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional: reset mobile drawer state on navigation
        setOpenMobile(false);
    }, [location.pathname]);

    const handleOpenMobileChange = useCallback((open: boolean) => setOpenMobile(open), []);

    // Load all nav and user menu items
    const { navItems: allNavItems, isLoading: isNavLoading } = useAllNavItems();
    const { userMenuItems: allUserMenuItems } = useAllUserMenuItems(authState.accountId, authState.isAuthenticated);

    // Get nav items for the active config
    const navItems = useMemo(() => {
        if (!activeConfig) return null;
        const basePath = env.apps.find(app => app.appName === activeConfig.appName)?.basePath || '';
        return getNavItemsForBasePath(allNavItems, basePath);
    }, [activeConfig, allNavItems]);

    const homeNavItems = useMemo(() => {
        if (env.sidebar.navSource === 'server') return [];
        return normalizeNavItems(env.sidebar.navItems || [], '');
    }, []);

    // Show AskRyanWidget only on home page "/" or "/settings"
    // const shouldShowAskRyan = location.pathname === '/' || location.pathname === '/settings';
    const hasSingleRemoteNavApp = env.apps.length === 1 && Boolean(env.apps[0]?.importRemoteNavItems);
    const shouldShowRootNav = !hasSingleRemoteNavApp && (location.pathname === '/' || location.pathname === '');

    return (
        <FloatingRootProvider>
            <PendoProvider authState={authState}>
                <div style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {/*
                        Commenting out AskRyanWidget till ready to readd
                        <AskRyanWidget visible={shouldShowAskRyan} />
                    */}
                    <div className={`flex min-h-0 flex-1 ${isMobile ? 'flex-col' : 'flex-row'}`}>
                        <SidebarHost
                            navItems={navItems || []}
                            homeNavItems={homeNavItems}
                            isHomeApp={shouldShowRootNav}
                            rootNavLabel={env.sidebar.rootNavLabel}
                            currentAppName={activeConfig?.appName}
                            isCollapsed={isSidebarCollapsed}
                            onToggle={toggleSidebar}
                            userMenuItems={allUserMenuItems}
                            isNavLoading={isNavLoading}
                            isMobile={isMobile}
                            openMobile={openMobile}
                            onOpenMobileChange={handleOpenMobileChange}
                        />
                        <main
                            className="relative flex min-h-0 w-full min-w-0 flex-1 flex-col"
                            style={{ backgroundColor: '#ffffff' }}>
                            <div className="relative min-h-0 flex-1 overflow-y-auto">
                                <Outlet />
                            </div>
                        </main>
                    </div>
                </div>
            </PendoProvider>
        </FloatingRootProvider>
    );
}

/**
 * Shell layout wrapper for authenticated mode.
 */
function AuthenticatedShellLayout() {
    const authState = useMsalAuthState();
    return <ShellLayoutContent authState={authState} />;
}

/**
 * Shell layout - provides the main application shell with sidebar and header.
 *
 * Features:
 * - Sidebar with navigation items from federated apps
 * - Header with page title and actions
 * - Auto-collapse sidebar for certain apps
 * - AskRyan widget on specific pages
 *
 * Uses useMatches() to get the active app config from the current route,
 * eliminating the need for manual route matching.
 */
export function ShellLayout() {
    // Branch at the component level to avoid conditional hook calls
    if (isAuthDisabled) {
        return <ShellLayoutContent authState={DISABLED_AUTH_STATE} />;
    }

    return <AuthenticatedShellLayout />;
}
